import { Box, Button, CircularProgress } from '@mui/material'
import TextFieldNN from 'components/TextFieldNN'
import PasswordInput from 'components/PasswordInput'
import utils from 'helpers/utils'
import React, { useCallback, useMemo, useState } from 'react'
import useShowError from 'hooks/useShowError'
import useReCaptcha from 'hooks/useReCaptcha'
import axios from 'providers/axios'
import { KEYS, URL_APP } from 'helpers/config'
import SignUpWithLinkedInButton from './SignUpWithLinkedInButton'
import SignUpWithSlackButton from './SignUpWithSlackButton'
import { useParams, useSearchParams } from 'react-router-dom'
import TagManager from 'react-gtm-module'

const SignUpForm: React.FC = () => {
  const { productId } = useParams()
  const [searchParams] = useSearchParams()
  const showError = useShowError()
  const { reCaptchaLoaded, generateReCaptchaToken } = useReCaptcha()

  const [firstName, setFirstName] = useState({ value: '', error: true, touched: false })
  const [lastName, setLastName] = useState({ value: '', error: true, touched: false })
  const [companieName, setCompanieName] = useState({ value: '', error: true, touched: false })
  const [email, setEmail] = useState({ value: '', error: true, touched: false })
  const [password, setPassword] = useState({ value: '', error: true })

  const [loading, setLoading] = useState(false)

  const validateFirstName = useCallback((value: string) => {
    const isValid = value.length > 0
    setFirstName((message) => ({ ...message, error: !isValid }))
    return isValid
  }, [])

  const validateLastName = useCallback((value: string) => {
    const isValid = value.length > 0
    setLastName((message) => ({ ...message, error: !isValid }))
    return isValid
  }, [])

  const validateCompanieName = useCallback((value: string) => {
    const isValid = value.length > 0
    setCompanieName((message) => ({ ...message, error: !isValid }))
    return isValid
  }, [])

  const validateEmail = useCallback((value: string) => {
    const isValid = utils.isValidEmail(value)
    setEmail((message) => ({ ...message, error: !isValid }))
    return isValid
  }, [])

  const isFormValid = useMemo(() => {
    const validFirstName = validateFirstName(firstName.value)
    const validLastName = validateLastName(lastName.value)
    const validCompanieName = validateCompanieName(companieName.value)
    const validEmail = validateEmail(email.value)
    const validPassword = !password.error
    return validFirstName && validLastName && validCompanieName && validEmail && validPassword
  }, [
    validateFirstName,
    firstName.value,
    validateLastName,
    lastName.value,
    validateCompanieName,
    companieName.value,
    validateEmail,
    email.value,
    password.error,
  ])

  const onSubmit = () => {
    const signUp = async () => {
      if (!isFormValid) return
      setLoading(true)
      try {
        const authDevice = utils.getFullAuthDeviceData()
        const token = await generateReCaptchaToken('signUp')
        const { data } = await axios.post(
          '/sign-up',
          {
            firstName: firstName.value,
            lastName: lastName.value,
            companieName: companieName.value,
            email: email.value,
            password: password.value,
            authDevice,
            productId,
            invitedById: searchParams.get('invitedById') ?? undefined,
            overrideDefaultSignupAction: searchParams.get('overrideDefaultSignupAction') ?? undefined,
          },
          {
            headers: {
              [KEYS.RECAPTCHA_TOKEN]: token,
              [KEYS.RECAPTCHA_ACTION]: 'signUp',
            },
          }
        )
        if (data) {
          TagManager.dataLayer({
            dataLayer: {
              event: 'signupFormUpdate',
              signupStep: 'signup-form',
            },
          })
          const authDevice = data.authDevice
          utils.setChromeExtension(authDevice.deviceToken)
          window.location.href = URL_APP
          return
        }
      } catch (error) {
        showError(error)
        setLoading(false)
      }
    }

    signUp()
  }

  return (
    <Box mb="16px">
      <Box display="flex" gap="8px">
        <TextFieldNN
          id="first-name"
          label="First name"
          placeholder="John"
          value={firstName.value}
          onChange={(e) => setFirstName((state) => ({ ...state, value: e.target.value }))}
          onFocus={() => setFirstName((state) => ({ ...state, touched: false }))}
          onBlur={(e) => {
            setFirstName((state) => ({ ...state, touched: true, error: !validateFirstName(e.target.value) }))
          }}
          error={firstName.touched && firstName.error}
          helperText="First name is required"
        />
        <TextFieldNN
          id="last-name"
          label="Last name"
          placeholder="Doe"
          value={lastName.value}
          onChange={(e) => setLastName((state) => ({ ...state, value: e.target.value }))}
          onFocus={() => setLastName((state) => ({ ...state, touched: false }))}
          onBlur={(e) => {
            setLastName((state) => ({ ...state, touched: true, error: !validateLastName(e.target.value) }))
          }}
          error={lastName.touched && lastName.error}
          helperText="Last name is required"
        />
      </Box>

      <TextFieldNN
        id="company-name"
        label="Company name"
        placeholder="Acme Corp"
        value={companieName.value}
        onChange={(e) => setCompanieName((state) => ({ ...state, value: e.target.value }))}
        onFocus={() => setCompanieName((state) => ({ ...state, touched: false }))}
        onBlur={(e) => {
          setCompanieName((state) => ({ ...state, touched: true, error: !validateCompanieName(e.target.value) }))
        }}
        error={companieName.touched && companieName.error}
        helperText="Company name is required"
      />

      <TextFieldNN
        id="email"
        label="Email"
        type="email"
        placeholder="example@address.com"
        value={email.value}
        onChange={(e) => setEmail((state) => ({ ...state, value: e.target.value }))}
        onFocus={() => setEmail((state) => ({ ...state, touched: false }))}
        onBlur={(e) => {
          setEmail((state) => ({ ...state, touched: true, error: !validateEmail(e.target.value) }))
        }}
        error={email.touched && email.error}
        helperText={email.value.length > 0 ? 'Email is invalid' : 'Email is required'}
      />

      <PasswordInput
        label="Password"
        onChange={(state) => {
          setPassword({
            value: state.value,
            error: !state.isValid,
          })
        }}
        onKeyDownEnter={onSubmit}
      />

      <Button
        variant="contained"
        fullWidth
        disabled={!isFormValid || !reCaptchaLoaded || loading}
        onClick={onSubmit}
        sx={{ mt: '16px' }}
      >
        {loading ? <CircularProgress size="20px" /> : 'Sign up'}
      </Button>

      <Box display="flex" justifyContent="center" gap="16px" mt="16px">
        <SignUpWithLinkedInButton />
        <SignUpWithSlackButton />
      </Box>
    </Box>
  )
}

export default SignUpForm
